import React, {
  createRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import {
  fnGetDataSet,
  fnSave,
  fnGetTableById,
  callApi,
} from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
  getNoSlip,
  getTablebySql,
  setConfigToColumns,
  toKeyMap,
  setUmByFgUm,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global, globalMenu } from "@shared/libs/Global";
import { showPopupModal } from "@widgets/CommonModal";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SN_PUR_POR_REG_001_POP_01 from "@menu/PUR/SN_PUR_POR_REG_001/SN_PUR_POR_REG_001_POP_01";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-enterprise";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import ExcelUpload_S01_Validate from "@menu/PUR/SN_PUR_POR_REG_001/excel_upload/ExcelUpload_S01_Validate";
import { openS02 } from "./excel_upload/ExcelUploadModals01";
import { stripBlanksFromSaveData } from "./stripBlanks";
import {
  SlipNumberingFn,
  toNonEmptyString,
} from "@widgets/commonpopup/excel-upload";
import {
  buildJsAddinfoFromFlags,
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";
import { openAddInfoPopup } from "@widgets/commonpopup/sys/openAddInfoPopup";
import { openAddInfoRegPopup } from "@widgets/commonpopup/sys/openAddInfoRegPopup";
import { executeApprovalSubmit } from "@shared/utils/approval/approvalIntegration";
import { submitApproval } from "@shared/api/approvalSubmitApi";

const getNoSlipStrict: SlipNumberingFn = async (
  cd_module,
  no_line,
  cd_prefix,
  date,
  cd_menu
) =>
  toNonEmptyString(
    await getNoSlip(cd_module, no_line, cd_prefix, date, cd_menu),
    "전표번호가 비어 있습니다."
  );

// 저장 직전 공백 제거.
// 문자열 값에서 모든 공백(스페이스/탭/줄바꿈/전각공백)을 없앤다.
// 앞뒤만이 아니라 값 중간의 공백까지 지우므로 비고·품목명 같은 문장형 값도 붙는다.
// 삭제분(delete)은 키로 원본 행을 찾아 지우기 때문에 건드리지 않는다.
const stripAllBlanks = (changes: any) => {
  const stripRows = (rows: any[]) =>
    (rows ?? []).forEach((row: any) => {
      Object.keys(row).forEach((key) => {
        if (typeof row[key] === "string") {
          row[key] = row[key].replace(/[\s　]/g, "");
        }
      });
    });

  stripRows(changes?.insert);
  stripRows(changes?.update);
};

// 발주형태(cd_po) 콤보 항목. 화면에서 mas_potype 로 직접 만든다(메타 콤보에는 fg_trns 가 없다).
type PoTypeOption = { code: string; name: string; fg_trns: string };

// 발주형태 콤보는 전체를 보여주되, 기준 거래구분과 같은 것을 앞으로 올린다.
// 같은 그룹 안에서는 발주형태 코드순(마스터 조회 순서)을 유지한다.
const sortPoTypeOptions = (options: PoTypeOption[], fg_trns?: string) => {
  if (!fg_trns) return options;
  const matched = options.filter((op) => op.fg_trns === String(fg_trns));
  if (matched.length === 0 || matched.length === options.length) return options;
  return [
    ...matched,
    ...options.filter((op) => op.fg_trns !== String(fg_trns)),
  ];
};

// 거래구분(fg_trns) 기준 부가세구분(fg_vat) 기본값
const getFgVatByFgTrns = (fg_trns?: string) => {
  if (fg_trns === "2" || fg_trns === "3") return "23";
  if (fg_trns === "4" || fg_trns === "5") return "56";
  return "21";
};

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

// 발주등록
const SN_PUR_POR_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });
  const sheetref1 = useRef<any>(null);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    sheet1: [],
  });

  // 한글 주석: 발주형태(mas_potype) 원본 목록(마스터 조회 순서). 정렬 기준과 신규행 기본값 계산용.
  const poTypeOptionsRef = useRef<PoTypeOption[]>([]);
  // 한글 주석: grid1 의 cd_po 컬럼 js_combo 로 넘기는 "그 배열".
  // SnColumnsCreate 는 js_combo 가 배열이면 참조를 그대로 들고 있다가 콤보를 열 때 읽으므로,
  // 이 배열을 제자리에서 재정렬하면 컬럼을 다시 만들지 않고도 표시 순서를 바꿀 수 있다.
  const cdPoComboRef = useRef<PoTypeOption[]>([]);
  const [defauleCdpo, setdefauleCdpo] = useState<String>("");
  const [defauleYnapp, setdefauleYnapp] = useState<String>("");
  const [defauleYnAutoreq, setdefauleYnAutoreq] = useState<String>("");
  const [defauleYnAutopur, setdefauleYnAutopur] = useState<String>("");
  const [defauleYnAutorcv, setdefauleYnAutorcv] = useState<String>("");
  const [defauleYnTrade, setdefauleYnTrade] = useState<String>("");
  let drillF: any = false;
  const calmRef = useRef<any>(null);
  const originalQtRef = useRef<Map<string, any>>(new Map());
  const isProcessing = useRef(false);
  const [selectedDocu, setSelectedDocu] = useState<string>(""); // file 관련
  // 한글 주석: grid1(발주정보)/grid2(상세정보)별 부가항목 키(cd_flag) 목록
  // 렌더링에는 쓰이지 않고 조회/저장 시점에만 읽으므로 ref로 둔다.
  // (state로 두면 설정 저장 직후 같은 렌더의 onSearch가 옛 목록을 보게 된다)
  const addInfoFlagsRef = useRef<string[]>([]);
  const addInfoFlags2Ref = useRef<string[]>([]);
  const isApprovalVisible = config?.MAS0121?.dc_ctrl === "Y";

  const loadAddInfoFlags = async (
    idTarget = "grid1",
    flagsRef = addInfoFlagsRef
  ) => {
    try {
      const data: any = await fnGetDataSet({
        id: "sys_addinfo_pop_01_01",
        params: {
          cd_menu: Menuprops.cd_menu,
          id_target: idTarget,
        },
      });
      const flags = extractAddInfoFlags(data?.[0] || []);
      flagsRef.current = flags;
      return flags;
    } catch (error: any) {
      flagsRef.current = [];
      return [];
    }
  };

  // 한글 주석: 화면 메타(getInitFormData) 결과를 그리드 컬럼 설정으로 변환한다.
  const buildGridColumns = (rtn: any) => {
    const popup_apply = [
      { target: "cd_wh", source: "cd_wh_rcv" },
      { target: "nm_wh", source: "nm_wh_rcv" },
    ];
    const grid2 = rtn.grid.grid2.map((col: any) => {
      let newCol = { ...col };
      if (col.cd_column === "cd_item") {
        newCol.popup_apply = popup_apply;
      }
      return newCol;
    });

    // 한글 주석: 발주형태(cd_po) 콤보를 화면에서 직접 구성해 항목마다 fg_trns 를 실어둔다.
    // 항목은 전체를 다 보여주고, 순서만 포커스 행의 거래구분에 맞춰 바꾼다.
    const grid1 = rtn.grid.grid1.map((col: any) => {
      if (
        col.cd_column !== "cd_po" ||
        col.tp_column !== "SnComboBox" ||
        cdPoComboRef.current.length === 0
      ) {
        return col;
      }
      return { ...col, js_combo: cdPoComboRef.current };
    });

    return {
      grid1,
      grid2,
      sheet1: rtn.grid.sheet1,
    };
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData(rtn.formData);

        const sqlQuery2 = {
          sql: "select cd_po,nm_po,fg_trns,yn_app,yn_autoreq,yn_autopur,yn_autorcv,yn_trade from mas_potype where cd_firm = :cd_firm and yn_use = 'Y' order by cd_po",
          cd_firm: userInfo.cd_firm,
        };

        const result2: any = await getTablebySql(sqlQuery2);
        // 한글 주석: 발주형태 마스터는 콤보 목록과 [신규] 기본값 모두에서 쓴다.
        poTypeOptionsRef.current = (Array.isArray(result2) ? result2 : []).map(
          (po: any) => ({
            code: String(po.cd_po ?? ""),
            name: String(po.nm_po ?? po.cd_po ?? ""),
            fg_trns: String(po.fg_trns ?? ""),
          })
        );
        // 콤보에 넘길 배열은 참조를 유지해야 하므로 새로 만들지 않고 내용만 채운다.
        cdPoComboRef.current.splice(
          0,
          cdPoComboRef.current.length,
          ...poTypeOptionsRef.current
        );

        setColums(buildGridColumns(rtn));

        if (poTypeOptionsRef.current.length > 0) {
          setdefauleCdpo(result2[0].cd_po.toString());
          setdefauleYnapp(result2[0].yn_app.toString());
          setdefauleYnAutoreq(result2[0].yn_autoreq.toString());
          setdefauleYnAutopur(result2[0].yn_autopur.toString());
          setdefauleYnAutorcv(result2[0].yn_autorcv.toString());
          setdefauleYnTrade(result2[0].yn_trade.toString());
        }
        // 한글 주석: 초기 진입 시 부가항목 키 목록도 함께 로드한다.
        await loadAddInfoFlags("grid1", addInfoFlagsRef);
        await loadAddInfoFlags("grid2", addInfoFlags2Ref);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    const fetchCalm = async () => {
      if (!calmRef.current) {
        // 최초 1번만 조회
        const SYS014 = {
          sql: "select cd_flag, dc_ref from mas_codel where cd_firm = :cd_firm and cd_clas = 'SYS014'",
          cd_firm: userInfo.cd_firm,
        };
        calmRef.current = await getTablebySql(SYS014);
      }
    };
    fetchCalm();
  }, [userInfo.cd_firm]);

  const drilldownHandledRef = useRef<any>(null);
  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      // 조회·팝업 등으로 컬럼이 다시 만들어질 때마다 드릴다운이 재실행되지 않게(처리탭으로 되돌아가는 문제) 드릴다운 1건당 1번만.
      if (drilldownHandledRef.current !== Menuprops) {
        drilldownHandledRef.current = Menuprops;
        onActivate(Menuprops.nodeData);
      }
    }
  }, [columns, Menuprops]);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelDownLoad,
    onExcelUpload,
    onPrint,
  }));

  // 한글 주석: 거래구분(fg_trns)에 맞는 발주형태를 고른다.
  // 후보(cd_po)가 이미 그 거래구분이면 그대로, 아니면 같은 거래구분의 첫 건으로 바꾼다.
  // 거래구분이 없거나 해당 발주형태가 하나도 없으면 후보를 그대로 둔다.
  const pickCdPoByFgTrns = (cd_po: string, fg_trns?: string) => {
    if (!fg_trns) return cd_po;
    const matched = poTypeOptionsRef.current.filter(
      (po) => po.fg_trns === String(fg_trns)
    );
    if (matched.length === 0) return cd_po;
    const keep = matched.find((po) => po.code === String(cd_po));
    return (keep ?? matched[0]).code;
  };

  // 한글 주석: 발주형태 콤보 순서를 기준 거래구분에 맞춰 다시 깐다(항목은 전체 유지).
  // 컬럼 설정을 갈아끼우면 그리드 컬럼이 통째로 다시 만들어지므로,
  // js_combo 로 넘긴 배열을 splice 로 제자리 재정렬만 한다. 콤보를 열 때 이 배열을 읽는다.
  const applyCdPoComboOrder = (fg_trns?: string) => {
    const options = poTypeOptionsRef.current;
    if (options.length === 0) return;
    const sorted = sortPoTypeOptions(options, fg_trns);
    cdPoComboRef.current.splice(0, cdPoComboRef.current.length, ...sorted);
  };

  // 발주형태(cd_po) 마스터 1건 조회
  const getPoType = async (cd_po?: string) => {
    if (!cd_po) return null;
    try {
      const data: any = await fnGetTableById({
        id: "potype",
        params: { cd_firm: userInfo.cd_firm, cd_po: cd_po },
      });
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    } catch (error: any) {
      return null;
    }
  };

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_pur_por_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      sheetref1.current.resetUpdate();
      const data: any = await fnGetDataSet(params);

      // 한글 주석: 조회 결과의 js_addinfo를 동적 부가항목 컬럼으로 펼친다.
      const flags =
        addInfoFlagsRef.current.length > 0
          ? addInfoFlagsRef.current
          : await loadAddInfoFlags("grid1", addInfoFlagsRef);
      const flags2 =
        addInfoFlags2Ref.current.length > 0
          ? addInfoFlags2Ref.current
          : await loadAddInfoFlags("grid2", addInfoFlags2Ref);

      setDataSources({
        grid1: hydrateRowsWithAddInfo(data[0] || [], flags),
        grid2: hydrateRowsWithAddInfo(data[1] || [], flags2),
        sheet1: data[2] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        await showDialog("alter", "수정된 내용이 있습니다.");
        return;
      }

      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());

      // 검색폼에 선택된 발주형태를 우선 사용하고, 없으면 마스터 첫 건을 사용한다.
      // 단 검색폼에 거래구분이 선택돼 있으면 그 거래구분에 맞는 발주형태로 맞춘다.
      // (콤보에도 그 거래구분 항목이 맨 앞으로 오므로 기본값과 순서가 어긋나지 않는다)
      const cd_po = pickCdPoByFgTrns(
        searchInfo.cd_po || defauleCdpo || "",
        searchInfo.fg_trns
      );
      const poType = await getPoType(cd_po);

      const fg_trns = poType?.fg_trns ?? searchInfo.fg_trns ?? "1";
      const fg_vat = getFgVatByFgTrns(fg_trns);
      const yn_app = poType?.yn_app ?? defauleYnapp;
      const newRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: searchInfo.cd_biz,
        no_po: "",
        cd_po: cd_po,
        st_po: yn_app === "Y" ? "R" : "F",
        cd_dept: userInfo.cd_dept,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        dt_po: today,
        cd_exch: "000",
        rt_exch: 1,
        fg_vat: fg_vat,
        fg_trns: fg_trns ?? "1",
        tp_um: "top1",
        fg_po: "001",
        cd_gp: userInfo.cd_gp,
        nm_gp: userInfo.nm_gp,
        fg_money: poType?.fg_money ?? "1",
        tp_pur: poType?.tp_pur ?? "",
        fg_web: "",
        st_scm: "",
        yn_autopur: poType?.yn_autopur ?? defauleYnAutopur,
        yn_autorcv: poType?.yn_autorcv ?? defauleYnAutorcv,
        yn_autoreq: poType?.yn_autoreq ?? defauleYnAutoreq,
        yn_trade: poType?.yn_trade ?? defauleYnTrade,
        tp_um_vat: poType?.tp_um_vat ?? "1",
      };
      const addedRow = gridRefs.current["grid1"].current?.addRow(newRow);
      // 한글 주석: 신규 행의 거래구분 기준으로 발주형태 콤보 순서를 맞춘다.
      // (포커스 변경 이벤트가 안 오는 경우가 있어 행추가 직후에도 한 번 건다)
      applyCdPoComboOrder(fg_trns ?? "1");
      // gridRefs.current["grid1"].current?.setFocusedCell(
      //   addedRow.rowIndex,
      //   "cd_cust"
      // );
    } catch (error: any) {
      console.error("Error in inserting row and focusing:", error);
    }
  };

  const onAddRow = () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      if (drillF === "false") {
        const requiredValuesCheckH =
          gridRefs.current["grid1"].current.requiredValuesCheck();
        if (!requiredValuesCheckH) return false;
      }

      return gridRefs.current["grid2"].current?.addRow({});
    } catch (error: any) {}
  };

  const onBeforeAddRowL = () => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      const fg_trns = focusedNodeH.data?.fg_trns ?? "1";
      const fg_vat = getFgVatByFgTrns(fg_trns);
      const today = dateToString(new Date());
      return {
        cd_firm: focusedNodeH.data?.cd_firm,
        cd_biz: focusedNodeH.data?.cd_biz,
        dt_dlv: today,
        no_line: maxNoLine + 1,
        no_po: focusedNodeH.data?.no_po,
        dt_ship_plan: subtractOneDay(today),
        st_po: focusedNodeH.data?.st_po,
        yn_protect: "N",
        st_confirm_scm: "N",
        qt: 0,
        um: 0,
        qt_lc: 0,
        qt_req: 0,
        qt_rcv: 0,
        qt_rtn: 0,
        qt_cls: 0,
        fg_trns: fg_trns,
        fg_vat: fg_vat,
        qt_unit_po: 0,
        cd_cust: focusedNodeH.data?.cd_cust,
        nm_cust: focusedNodeH.data?.nm_cust,
      };
    } catch (error: any) {
      return false;
    }
  };

  //#endregion

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    // 발주상태(st_po)별 삭제 통제
    // P/F: 통제없음, R: 발주형태 승인사용(yn_app)='N'이면 통제, C: 무조건 통제
    // S(진행)는 기존 통제(의뢰/입고내역 존재 시) 유지
    const selectedRowsH: any[] =
      gridRefs.current["grid1"].current?.getSelectedRows() ?? [];
    for (const row of selectedRowsH) {
      // 저장 전 신규 행은 통제 대상 아님
      if (!row.no_po) continue;
      if (row.st_po === "C") {
        await showDialog(
          "alter",
          "마감된 건 입니다. 마감취소 후 삭제해주시기 바랍니다."
        );
        return;
      }
      if (row.st_po === "R" && row.yn_app !== "Y") {
        await showDialog(
          "alter",
          "승인된 건 입니다. 승인취소 후 삭제해주시기 바랍니다."
        );
        return;
      }
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current.deleteAllRows();
    gridRefs.current["grid1"].current.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;

    gridRefs.current["grid2"].current?.deleteSelectedRows();

    setFooterDataToRow();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      gridRefs.current["grid2"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const purPohChanges = gridRefs.current["grid1"].current.getChange();
      const purPolChanges = gridRefs.current["grid2"].current.getChange();

      // 이후 검증·채번·부가항목 생성이 모두 저장될 값 그대로를 보도록 여기서 먼저 공백을 턴다.
      // (applyFix 와 같이 행 객체를 그 자리에서 고치는 방식이다)
      stripAllBlanks(purPohChanges);
      stripAllBlanks(purPolChanges);

      if (config.PUR0005?.dc_ctrl === "N") {
        const applyFix = (rows: any[]) => {
          rows.forEach((row: any) => {
            if (row.qt !== undefined) row.qt_po = row.qt;

            if (row.dc_item_unit !== undefined)
              row.dc_unit_po = row.dc_item_unit;

            row.qt_unit_po = 1;

            if (row.um !== undefined) row.um_po = row.um;
          });
        };
        applyFix(purPolChanges.insert);
        applyFix(purPolChanges.update);
      }

      const headerInserts = purPohChanges.insert ?? [];
      const lineInserts = purPolChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        purPohChanges.modifiedRowcount === 0 &&
        purPolChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 품목코드 검증 — 채번(applySlip) 전에 막아 전표번호가 헛되이 소모되지 않게 한다
      const okItemCodes = await validateItemCodes(purPolChanges);
      if (!okItemCodes) return false;

      // no_contract 중복 체크 로직
      if (headerInserts.length > 0 && config.PUR0207?.dc_ctrl === "Y") {
        let nmColumn = "";
        for (const g of columns.grid1) {
          if (g.cd_column === "no_contract") nmColumn = g.nm_column;
        }

        const no_contract = headerInserts[0].no_contract;
        if (no_contract) {
          const result = await callApi("SN_PUR_POR_REG_001_checkNoContract", {
            cd_firm: userInfo.cd_firm,
            no_contract: no_contract,
          });
          const data = result.data;
          if (data?.dt_po) {
            const dupDtPo = data.dt_po;
            const formattedDupDtPo = `${dupDtPo.slice(0, 4)}-${dupDtPo.slice(4, 6)}-${dupDtPo.slice(6, 8)}`;
            const confirm = await showDialog(
              "confirm",
              `발주일 ${formattedDupDtPo} 기준, 중복된 ${nmColumn}가 존재합니다.\n저장하시겠습니까?`
            );
            if (!confirm) return;
          }
        }
      }

      const lineChanges = [
        ...(purPolChanges.insert ?? []),
        ...(purPolChanges.update ?? []),
      ];

      for (const row of lineChanges) {
        if (!row.no_prq) continue;

        const originalKey = `${row.no_prq}_${row.no_line_prq}`;
        const originalQt = originalQtRef.current.get(originalKey) ?? 0;
        if ((row.qt ?? 0) > originalQt.qt) {
          await showDialog("alter", "구매품의량을 초과하였습니다.");
          return false;
        }
      }

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const noSlip = await applySlip({
        cd_column: "no_po",
        cd_module: Menuprops.cd_module,
        no_line: 3,
        cd_prefix: "",
        dt: focusedRowNodeH.data.dt_po,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [purPohChanges, purPolChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 한글 주석: 저장 시점에 flags가 비어있으면 재조회하여 누락 저장을 방지한다.
      const flagsForSave =
        addInfoFlagsRef.current.length > 0
          ? addInfoFlagsRef.current
          : await loadAddInfoFlags("grid1", addInfoFlagsRef);
      const flagsForSave2 =
        addInfoFlags2Ref.current.length > 0
          ? addInfoFlags2Ref.current
          : await loadAddInfoFlags("grid2", addInfoFlags2Ref);

      const purPohWithAddInfo = {
        ...purPohChanges,
        insert: (purPohChanges.insert ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave),
        })),
        update: (purPohChanges.update ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave),
        })),
      };

      //  자동의뢰 필수값 검증 (현재 선택 헤더가 yn_autoreq=Y 인 경우만)
      const okAutoReq = await validateAutoReqRequired(purPolChanges);
      if (!okAutoReq) return false;

      // 저장 전 공백 제거 (코드/번호 계열은 안쪽 공백까지, 그 외는 앞뒤만)
      const saveData = stripBlanksFromSaveData({
        pur_poh: purPohWithAddInfo,
        pur_pol: purPolChanges,
      });
      const ok = await fnSave("SN_PUR_POR_REG_001_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid1"].current.resetUpdate();
      originalQtRef.current.clear();
      return true;
    } catch (error: any) {
      gridRefs.current["grid2"].current.resetUpdate(true);
      gridRefs.current["grid1"].current.resetUpdate(true);
      return false;
    }
  };

  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        dt_from: nodeData.dt_start,
        dt_to: nodeData.dt_start,
        fg_trns: "1",
        cd_cust: "",
        cd_gp: "",
        cd_emp: "",
        fg_search: nodeData.no_po ?? "",
        st_po: "",
        chk: "",
        tp_umvat: "1",
        cd_pjt: "",
      };
      setFormData(selectedData);
      onSearch();
    } else if ((Menuprops.figure as string) === "fg_drilldown.Registration") {
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
      if (
        modifiedRowsH.modifiedRowcount > 0 ||
        modifiedRowsL.modifiedRowcount > 0
      ) {
        const confirm = await showDialog(
          "confirmCancel",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm === true) {
          await onSave();
          await setDrilldown();
          return;
        } else if (confirm === false) {
          setDataSources({ grid1: [], grid2: [], sheet1: [] });
          gridRefs.current["grid1"].current.resetUpdate();
          gridRefs.current["grid2"].current.resetUpdate();
          await setDrilldown();
          return;
        } else {
          return;
        }
      } else {
        await setDrilldown();
      }
    }
    drillF = false;
  };

  const setDrilldown = async () => {
    isProcessing.current = true;
    let param: any;
    let drill: any;

    if (Menuprops.originMenu === "SN_PUR_APP_REG_001") {
      drill = {
        cd_firm: userInfo?.cd_firm,
        no_app: Menuprops.nodeData[0],
        no_line: Menuprops.nodeData[1],
      };
      param = {
        id: "sn_pur_por_reg_001_drill_app", //구매품의등록
        params: drill,
      };
    } else {
      drill = {
        cd_firm: userInfo?.cd_firm,
        no_prq: Menuprops.nodeData[0],
        no_line: Menuprops.nodeData[1],
      };
      param = {
        id: "sn_pur_por_reg_001_drill", //구매요청등록
        params: drill,
      };
    }

    const data = await fnGetDataSet(param);

    const headerResult = data[0];
    const lineResult = data[1];
    if (headerResult) {
      drillF = true;
      const cd_module: any = Menuprops.cd_module;
      headerResult.forEach(async (header: any, idx: number) => {
        const no_slip = await getNoSlip(
          cd_module,
          3,
          "",
          header.dt_app,
          Menuprops.cd_menu
        );
        const addedRowNode = gridRefs.current["grid1"].current?.addRow(header);
        if (addedRowNode) {
          addedRowNode.setDataValue("no_po", no_slip);
        }
        // 해당 헤더에 맞는 라인만 필터링
        const relatedLines = lineResult.filter(
          (line: any) => line.cd_cust === header.cd_cust
        );

        for (const result of relatedLines) {
          // cd_wh 없을 때만 DB 조회
          if (!result["cd_wh"] && result["cd_item"]) {
            const sql = {
              sql: `select mi.cd_wh_rcv, mw.nm_wh, mi.yn_lot
            from mas_item mi
            left outer join mas_wh mw
              on mi.cd_firm = mw.cd_firm
             and mi.cd_wh_rcv = mw.cd_wh
           where mi.cd_firm = '${userInfo.cd_firm}'
             and mi.cd_item = '${result["cd_item"]}';`,
            };
            const data: any = await getTablebySql(sql);

            if (data && data.length > 0) {
              const row = data[0];
              // result 객체에 직접 주입
              result["cd_wh"] = row.cd_wh_rcv;
              result["nm_wh"] = row.nm_wh;
              result["yn_lot"] = row.yn_lot;
            }
          }
          const addedRowNode =
            gridRefs.current["grid2"].current?.addRow(result);
          // 비교하기 위함
          const key = `${result.no_prq}_${result.no_line_prq}`;
          originalQtRef.current.set(key, {
            qt: addedRowNode.data.qt ?? 0,
            no_app: addedRowNode.data.no_app,
            no_line_app: addedRowNode.data.no_line_app,
            no_prq: addedRowNode.data.no_prq,
            no_line_prq: addedRowNode.data.no_line_prq,
          });
        }
      });
    }
    isProcessing.current = false;
  };

  const onOpenAddInfoPopup = async (idTarget = "grid1") => {
    // id_target에 해당하는 현재 컬럼 메타를 설정 팝업으로 전달한다.
    const columnInfos =
      idTarget === "grid2" ? columns.grid2 || [] : columns.grid1 || [];
    const saved = await openAddInfoPopup({
      cd_menu: Menuprops.cd_menu!,
      id_target: idTarget,
      columnInfos,
    });
    if (!saved) return;

    const rtn: any = await getInitFormData();
    setFormControlData(rtn.formControlData);
    setFormData((prev: any) => ({
      ...(rtn.formData || {}),
      ...(prev || {}),
    }));
    setColums(buildGridColumns(rtn));

    // 한글 주석: 항목설정 변경 후 부가항목 키 목록을 다시 로드한다.
    // ref에 즉시 반영되므로 바로 아래 onSearch가 갱신된 목록으로 조회한다.
    idTarget === "grid2"
      ? await loadAddInfoFlags(idTarget, addInfoFlags2Ref)
      : await loadAddInfoFlags(idTarget, addInfoFlagsRef);

    await onSearch();
  };

  // grid1 헤더 우클릭 추가 메뉴를 처리한다.
  const onGrid1ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    if (event.id === "extra_item_setting") {
      await onOpenAddInfoPopup("grid1");
    }
  };

  // grid2 헤더 우클릭 추가 메뉴를 처리한다.
  const onGrid2ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    if (event.id === "extra_item_setting") {
      await onOpenAddInfoPopup("grid2");
    }
  };

  const handleContextMenu = (params: any) => {
    switch (params?.id) {
      case "wf_sn_pur_por_info":
        (async () => {
          // 부가항목 등록/수정은 현재 선택된 견적 헤더 row를 기준으로 처리한다.
          const rowNode = gridRefs.current["grid1"].current.focusedRowNode();
          if (!rowNode) {
            await showDialog("alter", "선택된 행이 없습니다.");
            return;
          }
          if (!Menuprops.cd_menu) {
            await showDialog("alter", "메뉴 코드가 없습니다.");
            return;
          }

          const result = await openAddInfoRegPopup({
            title: "발주부가항목",
            cd_menu: Menuprops.cd_menu,
            id_target: "grid1",
            rowData: { ...rowNode.data },
            width: "980px",
            height: "760px",
            rememberSizeKey: "SN_PUR_POR_REG_001",
          });
          if (!result || result === "close") return;

          // 팝업에서 반환한 값은 grid1 row에 반영하고, 저장 시 js_addinfo로 다시 묶는다.
          const values = result?.values ?? {};
          Object.keys(values).forEach((key) => {
            rowNode.setDataValue(key, values[key]);
          });
        })();
        break;
      default:
        break;
    }
  };

  const onExcelDownLoad = async () => {
    try {
      const displayedCols = sheetref1.current?.getAllDisplayedColumns() || [];
      const columnKeys = displayedCols.map((c: any) => c.getColId());
      sheetref1.current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onExcelUpload = async () => {
    try {
      const params = {
        id: "sn_pur_bas_reg_001_01",
        params: {
          cd_firm: userInfo?.cd_firm,
          cd_biz: userInfo?.cd_biz,
          str_search: "",
        },
      };

      const raw = await fnGetDataSet(params);
      const sqlQuery = {
        sql: "select cd_flag from mas_codel where cd_firm = :cd_firm and cd_clas ='PUR001' limit 1",
        cd_firm: userInfo.cd_firm,
      };
      const data: any = await getTablebySql(sqlQuery);
      const typeMap = toKeyMap(raw, { key: "cd_po", candidates: ["CD_PO"] });
      // 초기값
      const provideDefaults = (row: any) => ({
        cd_biz: row.cd_biz ?? userInfo?.cd_biz,
        cd_dept: row.cd_biz ?? userInfo?.cd_dept,
        cd_emp: row.cd_emp ?? userInfo?.cd_emp,
        nm_emp: row.nm_emp ?? userInfo?.nm_emp,
        st_so: row.st_so ?? "F",
        // 발주형태가 매핑되면 아래 mappings.transform 이 yn_app 기준으로 다시 세팅한다.
        // 여기서는 매핑 실패 행이 NOT NULL 위반이 되지 않도록 화면 [신규]와 동일한 기본값만 준다.
        st_po: row.st_po ?? (defauleYnapp === "Y" ? "R" : "F"),
        fg_um: data[0].cd_flag ?? "001",
        fg_po: "001",
        tp_um: row.tp_um ?? "001",
      });

      const modalRef = createRef<any>();
      const childRef = createRef<any>();
      const s01 = (
        <ExcelUpload_S01_Validate
          ref={childRef}
          modalRef={modalRef}
          Menuprops={Menuprops}
          grouping={["no_po", "cd_cust", "dt_po", "cd_po", "tp_um"]}
          provideDefaults={provideDefaults}
          mappings={[
            {
              key: "cd_po",
              map: typeMap,
              // 행별 발주형태의 승인사용여부(yn_app)로 발주상태 결정 (화면 [신규]와 동일 규칙)
              transform: (_row: any, base: any) => ({
                st_po: base?.yn_app === "Y" ? "R" : "F",
              }),
            },
          ]}
        />
      );

      const result = await showPopupModal({
        title: "발주 엑셀 업로드",
        children: s01,
        width: "80%",
        height: "90VH",
        okayBtnText: "다음",
        checkData: false,
      });
      if (!result) return;

      const ok = await openS02({
        ...result, // validRows, headerColumns, detailColumns, grouping
        controller: "SN_PUR_POR_REG_001_SAVE_01",
        numbering: {
          fn: getNoSlipStrict,
          options: {
            cd_module: "PUR",
            no_line: 3,
            cd_prefix: "",
            dateField: "dt_po",
            cd_menu: Menuprops.cd_menu,
          },
        },
        tables: { header: "pur_poh", detail: "pur_pol" },
        fieldMap: {
          headerNoField: "no_po",
          detailNoField: "no_po",
          lineNoField: "no_line",
        },
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onPrint = async () => {
    gridRefs.current["grid1"].current?.stopEditing();
    gridRefs.current["grid2"].current?.stopEditing();

    const searchInfo = searchRef.current.getFormdata();
    const selectedRow = gridRefs.current["grid1"].current?.getSelectedRows();
    if (selectedRow.length < 1) {
      await showDialog("alter", "선택된 발주정보가 없습니다.");
      return;
    }
    return {
      cd_firm: userInfo.cd_firm,
      cd_biz: searchInfo.cd_biz,
      no_po: selectedRow[0].no_po,
      dc_mail_send_address: selectedRow[0].dc_email_cust1,
    };
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "btnApply") {
      const purPohChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (purPohChanges.length === 0) {
        await showDialog("alter", "신규행을 추가하시기 바랍니다.");
        return;
      }

      const selectedFormData = searchRef.current.getFormdata();
      const selectedData = {
        cd_biz: selectedFormData.cd_biz,
        cd_cust: purPohChanges[0]?.cd_cust ?? "",
        nm_cust: purPohChanges[0]?.nm_cust ?? "",
        dt_from: selectedFormData.dt_from,
        dt_to: selectedFormData.dt_to,
      };

      const component = (
        <SN_PUR_POR_REG_001_POP_01 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "품의적용",
        children: component,
      });
      if (!result || result === "close") return;

      const headerResult = result[0];
      const lineResult: any[] = result[1];

      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedRowNodeH && headerResult[0]) {
        // for (const key in headerResult[0]) {
        //   focusedRowNodeH.setDataValue(key, headerResult[0][key]);
        // }
        focusedRowNodeH.setDataValue(
          "cd_cust",
          headerResult[0].cd_cust,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "nm_cust",
          headerResult[0].nm_cust,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "nm_cust_sort",
          headerResult[0].nm_cust_sort,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "cd_exch",
          headerResult[0].cd_exch,
          "noEvent"
        );
        focusedRowNodeH.setDataValue(
          "rt_exch",
          headerResult[0].rt_exch,
          "noEvent"
        );
      }

      lineResult.map((result: any) => {
        if (headerResult[0]) {
          const addedNode = onAddRow();
          if (addedNode) {
            // for (const key in result) {
            //   addedNode.setDataValue(key, result[key]);
            // }
            addedNode.setDataValue("cd_cust", lineResult[0].cd_cust, "noEvent");
            addedNode.setDataValue("nm_cust", lineResult[0].nm_cust, "noEvent");
            addedNode.setDataValue(
              "nm_cust_sort",
              lineResult[0].nm_cust_sort,
              "noEvent"
            );
            addedNode.setDataValue("cd_exch", lineResult[0].cd_exch, "noEvent");
            addedNode.setDataValue("rt_exch", lineResult[0].rt_exch, "noEvent");
            addedNode.setDataValue("cd_item", result.cd_item, "noEvent");
            addedNode.setDataValue("nm_item", result.nm_item, "noEvent");
            addedNode.setDataValue(
              "dc_item_spec",
              result.dc_item_spec,
              "noEvent"
            );
            addedNode.setDataValue(
              "dc_item_unit",
              result.dc_item_unit,
              "noEvent"
            );
            addedNode.setDataValue("yn_lot", result.yn_lot, "noEvent");
            addedNode.setDataValue("dt_dlv", result.dt_dlv, "noEvent");
            addedNode.setDataValue(
              "dt_ship_plan",
              result.dt_ship_plan,
              "noEvent"
            );
            addedNode.setDataValue("cd_wh", result.cd_wh, "noEvent");
            addedNode.setDataValue("nm_wh", result.nm_wh, "noEvent");
            addedNode.setDataValue("qt", result.qt_app, "noEvent");
            addedNode.setDataValue("um", result.um ?? 0, "noEvent");
            addedNode.setDataValue(
              "no_line_app",
              result.no_line || 0,
              "noEvent"
            );
            addedNode.setDataValue("no_app", result.no_app || 0, "noEvent");
            addedNode.setDataValue("cd_pjt", result.cd_pjt, "noEvent");
            addedNode.setDataValue("nm_pjt", result.nm_pjt, "noEvent");
            addedNode.setDataValue("no_wbs", result.no_wbs, "noEvent");
            addedNode.setDataValue("no_cbs", result.no_cbs, "noEvent");
            const key = `${result.no_prq}_${result.no_line_prq}`;
            originalQtRef.current.set(key, {
              qt: addedNode.data.qt ?? 0,
              no_app: addedNode.data.no_app,
              no_line_app: addedNode.data.no_line_app,
              no_prq: addedNode.data.no_prq,
              no_line_prq: addedNode.data.no_line_prq,
            });
          }
        }
      });
      setFooterDataToRow();
    }
    if (id == "aButton_Approve") {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
      const hasModified =
        modifiedRowsH.modifiedRowcount > 0 ||
        modifiedRowsL.modifiedRowcount > 0;
      if (hasModified) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장 후 결재상신 하시겠습니까?"
        );
        if (!confirm) return;
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }

      if (!focusedNodeH?.data?.no_po) {
        await showDialog("alter", "결재상신할 발주건을 선택해 주세요.");
        return;
      }

      if (focusedNodeH.data.st_po !== "F") {
        await showDialog("alter", "미승인 상태인 건만 결재상신할 수 있습니다.");
        return;
      }

      await executeApprovalSubmit({
        menuCode: "SN_PUR_POR_REG_001",
        noRef: focusedNodeH.data.no_po,
        userInfo: {
          cd_firm: userInfo.cd_firm,
          cd_biz: userInfo.cd_biz,
          cd_dept: userInfo.cd_dept,
          cd_emp: userInfo.cd_emp,
          cd_user: userInfo.cd_user,
        },
        submitApproval,
        showDialog,
        openWindow: (url) => window.open(url),
        noUrlMessage: "결재창 URL을 수신하지 못했습니다.",
      });
      return;
    }

    if (id == "aButton_ApproveORCancel") {
      const purAphChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (purAphChanges.length != 0) {
        await showDialog(
          "alter",
          "수정중인 건이 있습니다. 저장 후 진행하시기 바랍니다."
        );
        return;
      }

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH) {
        let qt_rcv = false;
        if (focusedNodeH.data?.st_po == "R") {
          gridRefs.current["grid2"].current.forEachFilteredNode(
            (node: RowNode) => {
              if (node.data.qt_rcv > 0) {
                showDialog("alter", `발주 진행된 건 입니다.`);
                qt_rcv = true;
                return;
              }
            }
          );
        }
        if (qt_rcv) return;
        let active = false;
        let str_st_po;
        if (focusedNodeH.data?.st_app == "F") {
          str_st_po = "R";
          active = true;
        } else if (focusedNodeH.data?.st_app == "R") {
          str_st_po = "F";
          active = true;
        }
        if (active) {
          focusedNodeH.setDataValue("st_po", str_st_po);
          const isSaved = await onSave();
          if (isSaved)
            gridRefs.current["grid2"].current.applyFilters({
              no_po: focusedNodeH.data.no_po,
            });
        }
      }
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  // const onFocusedRowChanged = (event: any) => {
  //   const selectedRow = event; // 클릭한 행 데이터
  //   if (selectedRow) {
  //     const no_po = selectedRow.no_po;
  //     gridRefs.current["grid2"].current.applyFilters({ no_po: no_po });
  //     gridRefs.current["grid2"].current.selectAndFocusRow(0);
  //   } else {
  //     return;
  //   }
  // };
  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      // 배열로 만들어서 필터링 함수 호출
      const no_po = selectedRow.no_po;

      setSelectedDocu(no_po);
      // 한글 주석: 포커스 행의 거래구분에 맞춰 발주형태 콤보 순서를 맞춘다.
      applyCdPoComboOrder(selectedRow.fg_trns);
    } else {
      return;
    }
  };

  const onCellValueChanged1 = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "cd_cust") {
      await setUmByFgUm(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "dt_po",
        rowData.tp_um
      );
    }

    if (colId === "fg_trns") {
      const fg_trns = rowData?.fg_trns ?? "1";
      const fg_vat = getFgVatByFgTrns(fg_trns);
      rowNode.setDataValue("fg_vat", fg_vat);
      // 한글 주석: 거래구분이 바뀌면 발주형태 콤보 순서도 다시 맞추고,
      // 거래구분과 안 맞게 된 발주형태는 같은 거래구분의 첫 건으로 바꿔준다.
      applyCdPoComboOrder(fg_trns);
      const nextCdPo = pickCdPoByFgTrns(rowData?.cd_po ?? "", fg_trns);
      if (nextCdPo !== (rowData?.cd_po ?? "")) {
        rowNode.setDataValue("cd_po", nextCdPo);
      }
      gridRefs.current["grid2"].current.forEachFilteredNode((node: RowNode) => {
        node.setDataValue("fg_trns", fg_trns, "noEvent");
        node.setDataValue("fg_vat", fg_vat, "noEvent");
      });
    }
    if (colId === "cd_po") {
      const focusedRowNode = gridRefs.current["grid1"].current.focusedRowNode();
      const poType = await getPoType(focusedRowNode.data?.cd_po ?? "");
      if (poType) {
        focusedRowNode.setDataValue("fg_trns", poType.fg_trns);
        focusedRowNode.setDataValue("fg_vat", getFgVatByFgTrns(poType.fg_trns));
        focusedRowNode.setDataValue("yn_trade", poType.yn_trade);
        focusedRowNode.setDataValue("yn_autoreq", poType.yn_autoreq);
        focusedRowNode.setDataValue("yn_autorcv", poType.yn_autorcv);
        focusedRowNode.setDataValue("yn_autopur", poType.yn_autopur);
        focusedRowNode.setDataValue("fg_money", poType.fg_money);
        focusedRowNode.setDataValue("tp_pur", poType.tp_pur);
        focusedRowNode.setDataValue("tp_um_vat", poType.tp_um_vat);
        focusedRowNode.setDataValue("st_po", poType.yn_app === "Y" ? "R" : "F");
      }
    }
    CalAmBulk(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onCellValueChanged2 = async (event: CellValueChangedEvent) => {
    if (!event) return;
    if (isProcessing.current) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedRowNodeH = gridRefs.current["grid1"].current.focusedRowNode();

    if (colId === "fg_vat") {
      const fg_trns = focusedRowNodeH.data.fg_trns;
      const fg_vat = rowData.fg_vat;
      if (fg_trns === "1") {
        if (fg_vat === "11") {
          rowNode.setDataValue("fg_vat", "21");
        } else if (fg_vat === "16") {
          rowNode.setDataValue("fg_vat", "26");
        }
      } else if ((fg_trns === "2" || fg_trns === "3") && fg_vat !== "23") {
        rowNode.setDataValue("fg_vat", "23");
      } else if ((fg_trns === "4" || fg_trns === "5") && fg_vat !== "56") {
        rowNode.setDataValue("fg_vat", "56");
      }
    }

    if (colId === "dt_dlv") {
      const dtIsuPlan = subtractOneDay(rowData.dt_dlv);
      rowNode.setDataValue("dt_ship_plan", dtIsuPlan);
    }

    // 거래처품목을 넣은 뒤 품목을 다른 품목으로 바꾸면 둘이 틀어지므로 거래처품목을 비운다.
    // 거래처품목을 고르면서 빈 품목이 채워질 때(이전 값 없음)는 제외한다.
    // (품목이 있으면 거래처품목 도움창이 그 품목으로 걸러지므로 품목 값이 바뀌지 않는다)
    // 거래처품목을 비우면 그리드 공통 처리가 거래처품목에만 있는 항목만 지우고 품목 값은 남긴다.
    if (
      colId === "cd_item" &&
      rowData.cd_item_cust &&
      event.oldValue &&
      event.newValue !== event.oldValue
    ) {
      rowNode.setDataValue("cd_item_cust", "");
    }
    // 2026-10-16 창고 · 프로젝트 일괄적용(빈 줄 채우기 확인 창)은 막아 둔다 — 그리드에서 끌어 내리면 복사 · 붙여넣기가 되고,
    // 챗봇이 줄을 하나씩 복사할 때마다 확인 창이 떴다(사용자 결정). 되살릴 때를 위해 주석으로 남긴다
    // if (colId === "nm_wh") {
    //   const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
    //   if (rowCount === 1) return;
    //
    //   if (
    //     rowData.cd_wh === "" ||
    //     rowData.cd_wh === null ||
    //     rowData.cd_wh === undefined
    //   )
    //     return;
    //
    //   let apply = false;
    //   gridRefs.current["grid2"].current?.forEachFilteredNode(
    //     (node: RowNode) => {
    //       if (
    //         node.data.cd_wh === "" ||
    //         node.data.cd_wh === null ||
    //         node.data.cd_wh === undefined
    //       )
    //         apply = true;
    //     }
    //   );
    //   if (!apply) return;
    //
    //   const confirm = await showDialog(
    //     "confirm",
    //     "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
    //   );
    //   if (confirm) {
    //     gridRefs.current["grid2"].current?.forEachFilteredNode(
    //       (node: RowNode) => {
    //         if (
    //           node.data.cd_wh === "" ||
    //           node.data.cd_wh === null ||
    //           node.data.cd_wh === undefined
    //         ) {
    //           node.setDataValue("cd_wh", rowData.cd_wh);
    //           node.setDataValue("nm_wh", rowData.nm_wh);
    //         }
    //       }
    //     );
    //   }
    // }
    // if (colId === "nm_pjt") {
    //   const rowCount = gridRefs.current["grid2"].current.getDisplayedRowCount();
    //   if (rowCount === 1) return;
    //
    //   if (
    //     rowData.cd_pjt === "" ||
    //     rowData.cd_pjt === null ||
    //     rowData.cd_pjt === undefined
    //   )
    //     return;
    //
    //   let apply = false;
    //   gridRefs.current["grid2"].current?.forEachFilteredNode(
    //     (node: RowNode) => {
    //       if (
    //         node.data.cd_pjt === "" ||
    //         node.data.cd_pjt === null ||
    //         node.data.cd_pjt === undefined
    //       )
    //         apply = true;
    //     }
    //   );
    //   if (!apply) return;
    //
    //   const confirm = await showDialog(
    //     "confirm",
    //     "일괄적용하시겠습니까?(비어있는 칸이 채워집니다.)"
    //   );
    //   if (confirm) {
    //     gridRefs.current["grid2"].current?.forEachFilteredNode(
    //       (node: RowNode) => {
    //         if (
    //           node.data.cd_pjt === "" ||
    //           node.data.cd_pjt === null ||
    //           node.data.cd_pjt === undefined
    //         ) {
    //           node.setDataValue("cd_pjt", rowData.cd_pjt);
    //           node.setDataValue("nm_pjt", rowData.nm_pjt);
    //         }
    //       }
    //     );
    //   }
    // }
    if (colId === "cd_item") {
      await setUmByFgUm(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "dt_po",
        rowData.tp_um
      );
    }
    CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  /**
   * 저장 대상 상세행의 품목코드 검증 — 앞뒤에 공백이 섞였거나 품목마스터에 없으면 false.
   * 공백은 DB 비교로 걸러지지 않으므로(MySQL은 뒤쪽 공백을 무시한다) 조회 전에 문자열로 먼저 판단한다.
   */
  const validateItemCodes = async (purPolChanges: any) => {
    const rows = [
      ...(purPolChanges?.insert ?? []),
      ...(purPolChanges?.update ?? []),
    ];
    const codes = rows
      .map((row: any) => String(row?.cd_item ?? ""))
      .filter((code) => code !== "");
    if (codes.length === 0) return true;

    const uniqueCodes = Array.from(new Set(codes));
    const hasSpacePadded = uniqueCodes.some((code) => code !== code.trim());

    if (!hasSpacePadded) {
      try {
        const inList = uniqueCodes
          .map((code) => `'${code.replace(/'/g, "''")}'`)
          .join(",");
        const sqlQuery = {
          sql: `select cd_item from mas_item where cd_firm = :cd_firm and cd_item in (${inList})`,
          cd_firm: userInfo.cd_firm,
        };
        const data: any = await getTablebySql(sqlQuery);
        const found = new Set(
          (Array.isArray(data) ? data : []).map((row: any) => row.cd_item)
        );
        if (uniqueCodes.every((code) => found.has(code))) return true;
      } catch (error: any) {
        // 조회 자체가 실패한 경우까지 저장을 막지는 않는다 (서버가 같은 코드로 다시 거른다)
        return true;
      }
    }

    await showDialog(
      "error",
      "[오류] [ET001] ERROR: 품목코드가 존재하지 않습니다!"
    );
    return false;
  };

  const setFooterDataToRow = () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerData = gridRefs.current["grid2"].current.getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am", footerData.am);
      focusedNodeH.setDataValue("am_k", footerData.am_k);
      focusedNodeH.setDataValue("am_vat", footerData.am_vat);
      focusedNodeH.setDataValue("am_supply", footerData.am_supply);
    }
  };

  const onAfterPopupClose = async (event: any) => {
    const applyNodes = event.nodes;
    const colId = event.colId;
    if (colId === "cd_item") {
      await setUmByFgUm(
        gridRefs.current["grid1"],
        gridRefs.current["grid2"],
        "dt_po",
        "",
        applyNodes
      );
    }
  };

  function subtractOneDay(dateStr: string): string {
    if (!/^\d{8}$/.test(dateStr)) {
      console.warn("날짜 형식이 올바르지 않습니다.");
    }

    if (dateStr === "") {
      return "";
    }
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1;
    const day = parseInt(dateStr.substring(6, 8), 10);

    const date = new Date(year, month, day);
    date.setDate(date.getDate() - 1);

    const yyyy = date.getFullYear();
    const mm = (date.getMonth() + 1).toString().padStart(2, "0");
    const dd = date.getDate().toString().padStart(2, "0");

    return `${yyyy}${mm}${dd}`;
  }

  const validateAutoReqRequired = async (purPolChanges: any) => {
    const selNode = gridRefs.current["grid1"].current.focusedRowNode();
    if (!selNode) return true;

    const header = selNode.data || {};
    const need = (header.yn_autoreq ?? "").toString().toUpperCase() === "Y";
    if (!need) return true;

    const inserts = purPolChanges?.insert ?? [];
    const updates = purPolChanges?.update ?? [];
    const rows = [...inserts, ...updates];

    const hasMissing = rows.some(
      (row: any) => !(row?.cd_wh ?? "").toString().trim()
    );

    if (hasMissing) {
      await showDialog("error", "창고 값이 필수인 발주형태 입니다.");
      return false;
    }

    return true;
  };

  // const validateAutoReqRequired = async (purPolChanges: any) => {
  //   const selNodes = gridRefs.current["grid1"].current.focusedRowNode();
  //   if (selNodes.length === 0) return true;

  //   const header = selNodes[0].data || {};
  //   const need = (header.yn_autoreq ?? "").toString().toUpperCase() === "Y";
  //   if (!need) return true;

  //   const inserts = purPolChanges?.insert ?? [];
  //   const updates = purPolChanges?.update ?? [];

  //   // 컬럼명 → 타이틀 매핑 ===
  //   const colDefs = gridRefs.current["grid2"].current?.getColumnDefs?.() || [];
  //   const colTitleMap: Record<string, string> = {};
  //   colDefs.forEach((col: any) => {
  //     if (col.field && col.headerName) colTitleMap[col.field] = col.headerName;
  //   });

  //   const missing: string[] = [];

  //   //  타이틀 표시 ===
  //   const check = (row: any, idx: number, kind: "insert" | "update") => {
  //     const cd_wh = (row?.cd_wh ?? "").toString().trim();

  //     const cd_wh_title = colTitleMap["cd_wh"] || "cd_wh";

  //     if (!cd_wh) missing.push(`[${kind}] ${cd_wh_title} 누락 (row#${idx})`);
  //   };

  //   inserts.forEach((r: any, i: number) => check(r, i + 1, "insert"));
  //   updates.forEach((r: any, i: number) => check(r, i + 1, "update"));
  //   if (missing.length > 0) {
  //     await showDialog(
  //       "error",
  //       "자동의뢰(yn_autoreq=Y) 시 필수값 누락:\n" + missing.join("\n")
  //     );
  //     return false;
  //   }
  //   return true;
  // };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChanged1,
          onContextMenu: handleContextMenu,
          onExtraHeaderMenuClick: onGrid1ExtraHeaderMenuClick,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChanged2,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onAfterPopupClose: onAfterPopupClose,
          onExtraHeaderMenuClick: onGrid2ExtraHeaderMenuClick,
        },
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "발주정보",
              extraHeaderMenuItems: [
                { id: "extra_item_setting", name: "부가항목 설정" },
              ],
            }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_po"] }}
            header={
              <div>
                <SnButton
                  text={"품의적용"}
                  onClick={() => {
                    onGrid1BtnsClick("btnApply");
                  }}
                />
                {isApprovalVisible && (
                  <SnButton
                    text={"결재상신"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Approve");
                    }}
                  />
                )}
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "상세정보",
              extraHeaderMenuItems: [
                { id: "extra_item_setting", name: "부가항목 설정" },
              ],
            }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
      <div style={{ display: "none" }}>
        <SnGrid
          ref={sheetref1}
          columnConfig={grids.sheet1.columns}
          rowData={grids.sheet1.dataSource} // Datasource
          etcProps={{ isEditable: false }}
        />
      </div>
    </div>
  );
});

export default SN_PUR_POR_REG_001;
